import { lazy, type ComponentType } from "react";
import { logger } from "@/utils/logger";

type Importer<T> = () => Promise<{ default: T }>;

const RELOAD_FLAG_PREFIX = "lazy-reload:";

/**
 * A failed dynamic import is almost never permanent: in dev it happens when Vite
 * restarts or re-optimizes deps mid-navigation, in production when a deploy
 * replaces the chunk hashes a loaded tab still references.
 *
 * React.lazy caches the rejected promise, so without this wrapper a single blip
 * leaves the route permanently broken until the user reloads by hand.
 */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
}

async function importWithRetry<T>(
  importer: Importer<T>,
  name: string,
  attempts = 3,
  delayMs = 400
): Promise<{ default: T }> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const mod = await importer();
      if (attempt > 1) {
        logger.log(`[lazyWithRetry] Recovered "${name}" on attempt ${attempt}`);
      }
      // Successful load means any earlier reload attempt did its job.
      clearReloadFlag(name);
      return mod;
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error) || attempt === attempts) break;
      logger.warn(`[lazyWithRetry] Retrying "${name}" (attempt ${attempt} failed)`, error);
      await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
    }
  }

  // Retries exhausted. A stale document can't be fixed by more fetches, so
  // reload once - guarded so a genuinely broken build can't loop forever.
  if (isChunkLoadError(lastError) && !hasReloadFlag(name)) {
    setReloadFlag(name);
    logger.error(`[lazyWithRetry] Reloading page to recover "${name}"`, lastError);
    window.location.reload();
    // Keep Suspense pending while the reload happens.
    return new Promise<{ default: T }>(() => {});
  }

  throw lastError;
}

function flagKey(name: string) {
  return `${RELOAD_FLAG_PREFIX}${name}`;
}

function hasReloadFlag(name: string): boolean {
  try {
    return sessionStorage.getItem(flagKey(name)) !== null;
  } catch {
    return false;
  }
}

function setReloadFlag(name: string) {
  try {
    sessionStorage.setItem(flagKey(name), String(Date.now()));
  } catch {
    /* private mode / blocked storage - fall through to showing the error UI */
  }
}

function clearReloadFlag(name: string) {
  try {
    sessionStorage.removeItem(flagKey(name));
  } catch {
    /* ignore */
  }
}

/** Drop-in replacement for React.lazy that survives transient chunk load failures. */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: Importer<T>,
  name: string
) {
  return lazy(() => importWithRetry(importer, name));
}
